<template>
  <div>
    <top-bar heading="User Reports"></top-bar>
    <loading-spinner :loading="loading"></loading-spinner>
    <div class="container-fluid mt-3" v-show="!results && !loading">
      <b-card>
        <b-card-header>Date Range</b-card-header>
        <b-card-body class="p-3">
          <div class="row">
            <div class="col">
              <label class="control-label visible-ie8 visible-ie9" for="date_from">Date From</label>
              <flat-pickr :class="{'form-control': true, 'is-invalid': errors.has('date_from')}"
                          type="text"
                          placeholder="Date From"
                          name="date_from"
                          id="date_from"
                          :config="$dateOptions"
                          v-model="date_from">
              </flat-pickr>
            </div>
            <div class="col">
              <label class="control-label visible-ie8 visible-ie9" for="date_to">Date To</label>
              <flat-pickr
                      :class="{'form-control': true, 'is-invalid': errors.has('date_to')}"
                      type="text"
                      placeholder="Date To"
                      name="date_to"
                      id="date_to"
                      :config="$dateOptions"
                      v-model="date_to">
              </flat-pickr>
            </div>
          </div>
        </b-card-body>
      </b-card>
      <b-card>
        <b-card-header>Report Type</b-card-header>
        <b-card-body class="p-3">
          <div class="row">
            <div class="col">
              <label class="control-label visible-ie8 visible-ie9" for="report">Report</label>
              <select :class="{'form-control': true, 'is-invalid': errors.has('report')}"
                      type="text"
                      name="report"
                      id="report"
                      v-model="report">
                <option value="user_activity_log">User Activity Log</option>
                <option value="user_activity_report">User Activity Report</option>
                <option value="user_messages">User Messages</option>
                <option value="pending_signoff_report">Pending Signoff Report</option>
                <option value="users_list">Users List</option>
              </select>
            </div>
          </div>
        </b-card-body>
      </b-card>
      <b-card>
        <b-card-header>Users</b-card-header>
        <b-card-body>
          <vue-good-table
                  @on-selected-rows-change="selectionChanged"
                  :columns="columns"
                  ref="users_table"
                  :rows="users"
                  :select-options="{ enabled: true }"
                  :pagination-options="{
                    enabled: true,
                    mode: 'records',
                    perPage: 10,
                    position: 'bottom',
                    perPageDropdown: [10, 25, 50, 100],
                    dropdownAllowAll: false,
                    setCurrentPage: 2,
                    nextLabel: 'next',
                    prevLabel: 'prev',
                    rowsPerPageLabel: 'Rows per page',
                    ofLabel: 'of',
                    pageLabel: 'page', // for 'pages' mode
                    allLabel: 'All',
                  }"
                  :isLoading="loading">
          </vue-good-table>
        </b-card-body>
      </b-card>
      <b-card>
        <b-card-header>Generate Report</b-card-header>
        <b-card-body class="p-3">
          <loading-button :loading="loading" :disabled="!selectedUsers.length || !report" block @submit="generateReport">Generate</loading-button>
        </b-card-body>
      </b-card>
    </div>
    <div class="container-fluid mt-3" v-if="results">
      <div class="row mb-3">
        <div class="col">
          <download-csv :data="results" :name="fileName" class="btn btn-block btn-success d-block">
            <font-awesome-icon icon="download"></font-awesome-icon>
            Download CSV
          </download-csv>
        </div>
        <div class="col">
          <b-button block variant="danger" @click="reset()">
            <font-awesome-icon icon="undo-alt"></font-awesome-icon>
            Reset
          </b-button>
        </div>
      </div>
      <b-card no-body header="Results">
        <b-card-body>
          <b-table :items="results" responsive="lg" striped hover stacked="sm" :per-page="perPage" :current-page="currentPage"></b-table>
          <div class="ml-2 mt-3">
            <b-pagination size="md" :total-rows="results.length" v-model="currentPage" :per-page="perPage"></b-pagination>
          </div>
        </b-card-body>
      </b-card>
    </div>
  </div>
</template>

<script>
import JsonCSV from 'vue-json-csv';

export default {
  name: 'UserReports',
  data () {
    return {
      date_from: '',
      date_to: '',
      report: '',
      users: [],
      selectedUsers: [],
      loading: true,
      columns: [
        {
          label: 'First Name',
          field: 'name_first',
          sortable: true,
          filterOptions: {
            enabled: true, // enable filter for this column
            placeholder: 'Search...' // placeholder for filter input
          }
        },
        {
          label: 'Last Name',
          field: 'name_last',
          sortable: true,
          filterOptions: {
            enabled: true, // enable filter for this column
            placeholder: 'Search...' // placeholder for filter input
          }
        },
        {
          label: 'Trainer',
          field: 'trainer.name',
          sortable: true,
          filterOptions: {
            enabled: true, // enable filter for this column
            placeholder: 'Search...' // placeholder for filter input
          }
        },
        {
          label: 'RTO',
          field: 'rto.name',
          sortable: true,
          filterOptions: {
            enabled: true, // enable filter for this column
            placeholder: 'Search...' // placeholder for filter input
          }
        },
        {
          label: 'RTO Trainer',
          field: 'rto_trainer.name',
          sortable: true,
          filterOptions: {
            enabled: true, // enable filter for this column
            placeholder: 'Search...' // placeholder for filter input
          }
        }
      ],
      results: null,
      currentPage: 1,
      perPage: 10
    };
  },
  components: {'download-csv': JsonCSV},
  methods: {
    getUsers () {
      this.loading = true;
      let queryParams = {
        includes: 'employer,trainer,rto,rto_trainer',
        role_id: '6,7,8,9,10',
        enabled: 1
      };

      this.axios.get('/users', {params: queryParams}).then(rsp => {
        this.users = rsp.data.data;
        this.loading = false;
      });
    },
    selectionChanged (users) {
      this.selectedUsers = users.selectedRows;
    },
    generateReport () {
      let report = {
        users: this.selectedUsers.map(user => { return user.id; }),
        report: this.report,
        date_from: this.date_from,
        date_to: this.date_to
      };

      this.loading = true;

      this.axios.post('/reports', report).then(rsp => {
        this.results = rsp.data;
        this.loading = false;
      }).catch(err => {
        this.loading = false;
        this.$errorOut(err);
      });
    },
    reset () {
      this.date_from = '';
      this.date_to = '';
      this.report = '';
      this.selectedUsers = [];
      this.results = null;
      this.currentPage = 1;
      this.perPage = 10;
      this.$refs['users_table'].reset();
    }
  },
  computed: {
    fileName () {
      return this.$moment().format('YYYY_MM_DD') + '_' + this.report + '.csv';
    }
  },
  mounted () {
    this.getUsers();
  },
  metaInfo: {
    title: 'User Reports'
  }
};
</script>
